import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { isFulfilledAction, isPendingAction, isRejectedAction } from '../utils';
import { FeedMessagesAmount } from 'services/patientService';
import { asyncDashboardActions } from 'store/dashboard/thunks';
import { FeedItem, FeedListInfoResponse, GetPatientCasesParams, Patient, PatientListInfo } from 'types';

export type AsyncDashboardAction = keyof typeof asyncDashboardActions;

type AsyncActionsAreLoading = {
  [key in AsyncDashboardAction]: boolean;
};

export type FeedFilter = {
  page: number;
  size: number;
};

export interface DashboardState {
  asyncActionsAreLoading: AsyncActionsAreLoading;

  dashboardFilter: GetPatientCasesParams;

  dashboardNewPatientCases: Patient[];
  dashboardNewCasesFilter: GetPatientCasesParams;
  dashboardNewPatientCasesListInfo: PatientListInfo | null;

  dashboardLetterPatientCases: Patient[];
  dashboardLetterCasesFilter: GetPatientCasesParams;
  dashboardLetterPatientCasesListInfo: PatientListInfo | null;

  dashboardInTreatmentPatientCases: Patient[];
  dashboardInTreatmentCasesFilter: GetPatientCasesParams;
  dashboardInTreatmentPatientCasesListInfo: PatientListInfo | null;

  dashboardFinishedPatientCases: Patient[];
  dashboardFinishedCasesFilter: GetPatientCasesParams;
  dashboardFinishedPatientCasesListInfo: PatientListInfo | null;

  dashboardOutOfTreatmentPatientCases: Patient[];
  dashboardOutOfTreatmentCasesFilter: GetPatientCasesParams;
  dashboardOutOfTreatmentPatientCasesListInfo: PatientListInfo | null;

  feedAmount: FeedMessagesAmount;
  feedFilter: FeedFilter;
  feed: FeedItem[];
  feedInfo: Omit<FeedListInfoResponse, 'content'> | null;

  activeFeedTab: keyof FeedMessagesAmount;
}

const initialLoadingsState: AsyncActionsAreLoading = {
  ...Object.keys(asyncDashboardActions).reduce((acc, actionType) => {
    acc[actionType as AsyncDashboardAction] = false;
    return acc;
  }, {} as AsyncActionsAreLoading),
  getFeed: true,
  getPatientCasesDashboardFinished: true,
  getPatientCasesDashboardInTreatment: true,
  getPatientCasesDashboardLetter: true,
  getPatientCasesDashboardNew: true,
  getPatientCasesDashboardOutOfTreatment: true,
};

export const initialState: DashboardState = {
  activeFeedTab: 'newPatients',

  asyncActionsAreLoading: initialLoadingsState,

  dashboardFilter: { isArchived: false },

  dashboardNewPatientCases: [],
  dashboardNewPatientCasesListInfo: null,
  dashboardNewCasesFilter: {
    isArchived: false,
  },

  dashboardLetterPatientCases: [],
  dashboardLetterPatientCasesListInfo: null,
  dashboardLetterCasesFilter: {
    isArchived: false,
  },

  dashboardInTreatmentPatientCases: [],
  dashboardInTreatmentPatientCasesListInfo: null,
  dashboardInTreatmentCasesFilter: {
    isArchived: false,
  },

  dashboardFinishedPatientCases: [],
  dashboardFinishedPatientCasesListInfo: null,
  dashboardFinishedCasesFilter: {
    isArchived: false,
  },

  dashboardOutOfTreatmentPatientCases: [],
  dashboardOutOfTreatmentPatientCasesListInfo: null,
  dashboardOutOfTreatmentCasesFilter: {
    isArchived: false,
  },

  feedAmount: {
    finished: 0,
    inTreatment: 0,
    letterReview: 0,
    newPatients: 0,
    outOfTreatment: 0,
  },

  feedFilter: {
    page: 0,
    size: 20,
  },
  feed: [],
  feedInfo: null,
};

export const slice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setDashboardFilter: (state, { payload }: PayloadAction<GetPatientCasesParams>) => {
      state.dashboardFilter = payload;
      state.dashboardNewCasesFilter = payload;
      state.dashboardLetterCasesFilter = payload;
      state.dashboardInTreatmentCasesFilter = payload;
      state.dashboardFinishedCasesFilter = payload;
      state.dashboardOutOfTreatmentCasesFilter = payload;
    },
    setNewCasesFilter: (state, { payload }: PayloadAction<GetPatientCasesParams>) => {
      state.dashboardNewCasesFilter = payload;
    },
    setLetterCasesFilter: (state, { payload }: PayloadAction<GetPatientCasesParams>) => {
      state.dashboardLetterCasesFilter = payload;
    },
    setInTreatmentCasesFilter: (state, { payload }: PayloadAction<GetPatientCasesParams>) => {
      state.dashboardInTreatmentCasesFilter = payload;
    },
    setFinishedCasesFilter: (state, { payload }: PayloadAction<GetPatientCasesParams>) => {
      state.dashboardFinishedCasesFilter = payload;
    },
    setOutOfTreatmentCasesFilter: (state, { payload }: PayloadAction<GetPatientCasesParams>) => {
      state.dashboardOutOfTreatmentCasesFilter = payload;
    },
    setFeedFilter: (state, { payload }: PayloadAction<Partial<FeedFilter>>) => {
      state.feedFilter = { ...state.feedFilter, ...payload };
    },
    setActiveFeedTab: (state, { payload }: PayloadAction<keyof FeedMessagesAmount>) => {
      state.activeFeedTab = payload;
    },
    resetDashboardCasesBeforeUpdate: (
      state,
      { payload }: PayloadAction<('New' | 'Letter' | 'InTreatment' | 'Finished' | 'OutOfTreatment')[]>,
    ) => {
      payload.forEach((casesType) => {
        state[`dashboard${casesType}CasesFilter`].page = 0;
        state[`dashboard${casesType}PatientCasesListInfo`] = null;
        state[`dashboard${casesType}PatientCases`] = [];
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(asyncDashboardActions.getPatientCasesDashboardNew.fulfilled, (state, { payload }) => {
      const { content, ...info } = payload;

      if (!info.first) {
        state.dashboardNewPatientCases = [...state.dashboardNewPatientCases, ...content];
        state.dashboardNewPatientCasesListInfo = info;
        return;
      }
      state.dashboardNewPatientCases = content;
      state.dashboardNewPatientCasesListInfo = info;
    });
    builder.addCase(asyncDashboardActions.getPatientCasesDashboardLetter.fulfilled, (state, { payload }) => {
      const { content, ...info } = payload;

      if (!info.first) {
        state.dashboardLetterPatientCases = [...state.dashboardLetterPatientCases, ...content];
        state.dashboardLetterPatientCasesListInfo = info;
        return;
      }
      state.dashboardLetterPatientCases = content;
      state.dashboardLetterPatientCasesListInfo = info;
    });
    builder.addCase(asyncDashboardActions.getPatientCasesDashboardInTreatment.fulfilled, (state, { payload }) => {
      const { content, ...info } = payload;

      if (!info.first) {
        state.dashboardInTreatmentPatientCases = [...state.dashboardInTreatmentPatientCases, ...content];
        state.dashboardInTreatmentPatientCasesListInfo = info;
        return;
      }
      state.dashboardInTreatmentPatientCases = content;
      state.dashboardInTreatmentPatientCasesListInfo = info;
    });
    builder.addCase(asyncDashboardActions.getPatientCasesDashboardFinished.fulfilled, (state, { payload }) => {
      const { content, ...info } = payload;

      if (!info.first) {
        state.dashboardFinishedPatientCases = [...state.dashboardFinishedPatientCases, ...content];
        state.dashboardFinishedPatientCasesListInfo = info;
        return;
      }
      state.dashboardFinishedPatientCases = content;
      state.dashboardFinishedPatientCasesListInfo = info;
    });
    builder.addCase(asyncDashboardActions.getPatientCasesDashboardOutOfTreatment.fulfilled, (state, { payload }) => {
      const { content, ...info } = payload;

      if (!info.first) {
        state.dashboardOutOfTreatmentPatientCases = [...state.dashboardOutOfTreatmentPatientCases, ...content];
        state.dashboardOutOfTreatmentPatientCasesListInfo = info;
        return;
      }
      state.dashboardOutOfTreatmentPatientCases = content;
      state.dashboardOutOfTreatmentPatientCasesListInfo = info;
    });
    builder.addCase(asyncDashboardActions.getFeedMessagesAmount.fulfilled, (state, { payload }) => {
      state.feedAmount = payload;
    });
    builder.addCase(asyncDashboardActions.getFeed.fulfilled, (state, { payload }) => {
      const { content, ...info } = payload;

      if (!info.first) {
        state.feed = [...state.feed, ...content];
        state.feedInfo = info;
        return;
      }
      state.feed = content;
      state.feedInfo = info;
    });
    builder.addMatcher(isPendingAction, (state, action) => {
      const actionName = action.type.split('/')[1] as AsyncDashboardAction;
      state.asyncActionsAreLoading[actionName] = true;
    });
    builder.addMatcher(isFulfilledAction || isRejectedAction, (state, action) => {
      const actionName = action.type.split('/')[1] as AsyncDashboardAction;
      state.asyncActionsAreLoading[actionName] = false;
    });
  },
});

export const dashboardActions = { ...slice.actions, ...asyncDashboardActions };
export const dashboardReducer = slice.reducer;
